"use client";

import { useState, useEffect } from "react";
import {
  Advertisement,
  AdvertisementService,
  AdvertisementFilters,
} from "@/services/AdvertisementService";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  PlayIcon,
  PhotoIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import AdvertisementForm from "./AdvertisementForm";
import AdvertisementPreview from "./AdvertisementPreview";
import ConfirmDialog from "@/components/common/ConfirmDialog";

interface AdvertisementListProps {
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  initialLimit?: number;
  onLimitChange?: (limit: number) => void;
  initialFilters?: AdvertisementFilters;
  onFiltersChange?: (filters: AdvertisementFilters) => void;
}

export default function AdvertisementList({
  initialSearch = "",
  onSearchChange,
  initialPage = 1,
  onPageChange,
  initialLimit = 10,
  onLimitChange,
  initialFilters = { active: true },
  onFiltersChange,
}: AdvertisementListProps) {
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [currentLimit, setCurrentLimit] = useState(initialLimit);
  const [filters, setFilters] = useState<AdvertisementFilters>(initialFilters);
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");

  // Dialog states
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");

  // Sync state when the URL-driven props change (deep links like ?page=4 arrive
  // after mount, once the parent has parsed the query string)
  useEffect(() => {
    setSearchValue(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    setCurrentLimit(initialLimit);
  }, [initialLimit]);

  useEffect(() => {
    // Keep the previous object when values are equal so the fetch effect doesn't re-run
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(initialFilters) ? prev : initialFilters
    );
  }, [initialFilters]);

  // Fetch advertisements
  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await AdvertisementService.getAdvertisements(
        currentLimit,
        sortField,
        sortOrder,
        currentPage,
        searchValue,
        filters
      );

      setAdvertisements(response.data || []);
      setTotalCount(response.total || 0);
    } catch (err: any) {
      setError(err.message || "Failed to fetch advertisements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentLimit, searchValue, filters, sortField, sortOrder]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
    onSearchChange?.(value);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: AdvertisementFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    onFiltersChange?.(newFilters);
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortOrder("ASC");
    }
  };

  // Handle create/edit
  const handleOpenForm = (ad?: Advertisement) => {
    if (ad) {
      setSelectedAd(ad);
      setFormMode("edit");
    } else {
      setSelectedAd(null);
      setFormMode("create");
    }
    setShowForm(true);
  };

  // Handle preview
  const handlePreview = (ad: Advertisement) => {
    setSelectedAd(ad);
    setShowPreview(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedAd) return;

    try {
      await AdvertisementService.deleteAdvertisement(selectedAd.id);
      setShowDeleteConfirm(false);
      fetchAdvertisements();
    } catch (err: any) {
      setError(err.message || "Failed to delete advertisement");
    }
  };

  // Handle duplicate
  const handleDuplicate = async (ad: Advertisement) => {
    try {
      await AdvertisementService.duplicateAdvertisement(ad.id);
      fetchAdvertisements();
    } catch (err: any) {
      setError(err.message || "Failed to duplicate advertisement");
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (ad: Advertisement) => {
    try {
      await AdvertisementService.toggleAdvertisementStatus(ad.id, !ad.active);
      fetchAdvertisements();
    } catch (err: any) {
      setError(err.message || "Failed to toggle advertisement status");
    }
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / currentLimit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Advertisements</h2>
        <button
          onClick={() => handleOpenForm()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Advertisement
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search ads..."
              className="pl-10 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.active === null ? "all" : filters.active ? "active" : "inactive"}
            onChange={(e) => {
              const value = e.target.value;
              handleFilterChange({
                ...filters,
                active: value === "all" ? null : value === "active",
              });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {/* Type Filter */}
          <select
            value={filters.type || "all"}
            onChange={(e) => {
              const value = e.target.value;
              handleFilterChange({
                ...filters,
                type: value === "all" ? null : value as "ad_photo" | "ad_vid",
              });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Types</option>
            <option value="ad_photo">Photo Ads</option>
            <option value="ad_vid">Video Ads</option>
          </select>

          {/* Page Filter */}
          <select
            value={filters.page || "all"}
            onChange={(e) => {
              const value = e.target.value;
              handleFilterChange({
                ...filters,
                page: value === "all" ? null : value as "profile" | "session" | "both",
              });
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Pages</option>
            <option value="profile">Profile Page</option>
            <option value="session">Session Page</option>
            <option value="both">Both Pages</option>
          </select>
        </div>

        {/* Limit selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Show:</label>
          <select
            value={currentLimit}
            onChange={(e) => {
              const newLimit = parseInt(e.target.value);
              setCurrentLimit(newLimit);
              setCurrentPage(1);
              onLimitChange?.(newLimit);
            }}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
          <span className="text-sm text-gray-600">per page</span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("id")}
              >
                ID {sortField === "id" && (sortOrder === "ASC" ? "↑" : "↓")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preview
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("adtext")}
              >
                Text {sortField === "adtext" && (sortOrder === "ASC" ? "↑" : "↓")}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pages
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : advertisements.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  No advertisements found
                </td>
              </tr>
            ) : (
              advertisements.map((ad) => (
                <tr key={ad.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{ad.adid}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center">
                      {ad.type === "ad_vid" ? (
                        <>
                          <PlayIcon className="h-4 w-4 mr-1" />
                          Video
                        </>
                      ) : (
                        <>
                          <PhotoIcon className="h-4 w-4 mr-1" />
                          Photo
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {ad.type === "ad_photo" ? (
                      <img
                        src={ad.resource}
                        alt="Ad preview"
                        className="h-16 w-24 object-cover rounded cursor-pointer"
                        onClick={() => handlePreview(ad)}
                      />
                    ) : (
                      <div
                        className="h-16 w-24 bg-gray-200 rounded flex items-center justify-center cursor-pointer"
                        onClick={() => handlePreview(ad)}
                      >
                        <PlayIcon className="h-8 w-8 text-gray-500" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={ad.adtext}>
                      {ad.adtext}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex gap-2">
                      {ad.profilepage != null && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Profile (p{ad.profilepage})
                        </span>
                      )}
                      {ad.sessionpage != null && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          Session (p{ad.sessionpage})
                        </span>
                      )}
                      {ad.profilepage == null && ad.sessionpage == null && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          No placement
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={() => handleToggleStatus(ad)}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        ad.active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {ad.active ? (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handlePreview(ad)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Preview"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleOpenForm(ad)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(ad)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Duplicate"
                      >
                        <DocumentDuplicateIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAd(ad);
                          setShowDeleteConfirm(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => {
                const newPage = Math.max(1, currentPage - 1);
                setCurrentPage(newPage);
                onPageChange?.(newPage);
              }}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const newPage = Math.min(totalPages, currentPage + 1);
                setCurrentPage(newPage);
                onPageChange?.(newPage);
              }}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * currentLimit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * currentLimit, totalCount)}
                </span>{" "}
                of <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, currentPage - 1);
                    setCurrentPage(newPage);
                    onPageChange?.(newPage);
                  }}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                          onPageChange?.(page);
                        }}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? "z-10 bg-indigo-50 border-indigo-500 text-indigo-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return (
                      <span
                        key={page}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                      >
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  onClick={() => {
                    const newPage = Math.min(totalPages, currentPage + 1);
                    setCurrentPage(newPage);
                    onPageChange?.(newPage);
                  }}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Form Dialog */}
      {showForm && (
        <AdvertisementForm
          advertisement={selectedAd}
          mode={formMode}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            fetchAdvertisements();
          }}
        />
      )}

      {/* Preview Dialog */}
      {showPreview && selectedAd && (
        <AdvertisementPreview
          advertisement={selectedAd}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Advertisement"
        message={`Are you sure you want to delete advertisement #${selectedAd?.adid}? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}